/**
 * Auth service — talks ONLY to the onboarding BFF via httpClient.
 * All ROUTES come from the frozen BFF contract; types from auth contract.
 */

import { httpClient, setAccessToken } from './http-client';
import { ROUTES } from '@/contracts/bff';
import type {
  LoginResponse,
  RefreshResponse,
  RegisterResponse,
  LogoutResponse,
  OperatorSession,
  CheckEmailResponse,
} from '@/contracts/bff';
import type { LoginCredentials, RegisterCredentials, UserProfile } from '@/contracts/auth';

export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  return httpClient.post<LoginResponse>(ROUTES.authLogin, credentials);
}

/** Platform-operator Cognito login. Stores the ID token for subsequent provision calls. */
export async function cognitoLogin(credentials: LoginCredentials): Promise<LoginResponse> {
  const result = await httpClient.post<LoginResponse>(ROUTES.authCognitoLogin, credentials);
  setAccessToken(result.tokens.accessToken);
  return result;
}

export async function fetchSession(): Promise<OperatorSession> {
  return httpClient.get<OperatorSession>(ROUTES.authSession);
}

export async function register(credentials: RegisterCredentials): Promise<RegisterResponse> {
  return httpClient.post<RegisterResponse>(ROUTES.authRegister, credentials);
}

export async function refresh(): Promise<RefreshResponse> {
  return httpClient.post<RefreshResponse>(ROUTES.authRefresh);
}

export async function logout(): Promise<LogoutResponse> {
  try {
    return await httpClient.post<LogoutResponse>(ROUTES.authLogout);
  } finally {
    setAccessToken(null);
  }
}

/**
 * Probe whether an email already has an account. Used by the Account step in
 * register mode to route a returning merchant to sign-in before provisioning.
 */
export async function checkEmail(email: string): Promise<CheckEmailResponse> {
  return httpClient.post<CheckEmailResponse>(ROUTES.authCheckEmail, { email });
}

export async function getProfile(accessToken: string): Promise<UserProfile> {
  return httpClient.get<UserProfile>(ROUTES.usersMe, {
    Authorization: `Bearer ${accessToken}`,
  });
}
